import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import { useAlertPopup } from 'hooks/useAlertPopup';
import { authService } from 'services/auth/authService';

interface FormData {
  email: string;
}

export const useForgotPasswordForm = (onSuccess: (email: string) => void) => {
  const { t } = useTranslation('account');
  const { showAlertPopup } = useAlertPopup();

  const initialValues: FormData = { email: '' };

  const validationSchema = Yup.object().shape({
    email: Yup.string().trim().required(t('forgot-password.email-required')).email(t('forgot-password.email-invalid')),
  });

  const onSubmit = async (data: FormData) => {
    const email = data.email.trim().toLowerCase();

    try {
      await authService.requestPasswordReset(email);
      onSuccess(email);
    } catch (error: any) {
      handleError(error);
    }
  };

  const handleError = (error: any) => {
    const code = error.response?.data?.code || error.response?.data?.status;

    if (code === 'EMAIL_NOT_EXISTS' || error.response?.status === 404) {
      showAlertPopup({
        header: t('forgot-password.error-header'),
        description: t('forgot-password.error-email-not-exist'),
        firstButton: { label: t('forgot-password.error-confirm') },
        testId: 'forgot-password-error-alert',
      });
    } else if (code === 'EMAIL_USED_IN_SOCIAL_LOGIN') {
      showAlertPopup({
        header: t('forgot-password.error-header'),
        description: t('forgot-password.error-email-used-for-social-login'),
        firstButton: { label: t('forgot-password.error-confirm') },
        testId: 'forgot-password-error-alert',
      });
    } else if (code === 'EMAIL_NOT_CONFIRMED') {
      showAlertPopup({
        header: t('forgot-password.error-header'),
        description: t('forgot-password.error-email-not-confirmed'),
        firstButton: { label: t('forgot-password.error-confirm') },
        testId: 'forgot-password-error-alert',
      });
    } else {
      showAlertPopup({
        header: t('forgot-password.error-header'),
        description: t('forgot-password.error-unknown'),
        firstButton: { label: t('forgot-password.error-confirm') },
        testId: 'forgot-password-error-alert',
      });
    }
  };

  return useFormik({ initialValues, validationSchema, onSubmit });
};
